import { useEffect, useState } from 'react';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, FormControlLabel, Link, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';

import './Auth.css';

import { FloatingField } from '../../components';
import AuthLayaout from './AuthLayout';
import { notify } from 'components/Common/Toast';
import { RootState, store } from 'redux/store';
import { login } from 'redux/slices/auth/authSlice';
import MuiLoader from 'components/Common/Loader';
import Button from 'components/Common/Button';
import Routes from 'constants/routes';
import { supportEmail } from 'appConfig';
import { postAuthPath, readMemberInviteEmail } from 'utils/memberInviteToken';
import { readStaffInviteEmail } from 'utils/staffInviteToken';
import { getOrCreateDeviceId } from 'utils/deviceId';

const formSchema = yup
    .object({
        email: yup.string().required('email is required').email('Enter a valid email').lowercase().trim(),
        password: yup.string().required('Password is required'),
    })
    .required();

export interface LoginFormInputs {
    email: string;
    password: string;
}

function Login() {
    const authData = useSelector((state: RootState) => state.auth.data);
    const { accessToken } = authData || {};
    const [initialLoading, setInitialLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch<typeof store.dispatch>();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const locEmail = ((location.state as { email?: string } | null)?.email || '').toLowerCase().trim();
    const invitedEmail = (locEmail || readStaffInviteEmail() || readMemberInviteEmail() || '').toLowerCase().trim();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<LoginFormInputs>({
        resolver: yupResolver(formSchema),
        defaultValues: {
            email: invitedEmail,
            password: '',
        },
    });

    useEffect(() => {
        if (accessToken) {
            navigate(postAuthPath(Routes.home.main, Routes.auth.acceptMemberInvite, Routes.auth.acceptInvite), { replace: true });
        }
        setInitialLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- MOUNT ONLY, DO NOT ADD accessToken. This effect exists solely to skip the form for an already-authenticated visitor. submit() owns post-login routing and branches to /verify-email, /verify-2fa, /change-password or home. If accessToken is a dependency, this effect re-fires the moment a token lands and races submit() to home — bypassing the 2FA challenge, the email-verification gate and the forced password change. See docs/react-hooks-cleanup/01-phase1-protect-suppressions.md
    }, []);

    const submit = async (data: { [x: string]: any }) => {
        const email = data?.email;
        const password = data?.password;
        setIsLoading(true);
        try {
            let result: any = await dispatch(login({ email, password, deviceId: getOrCreateDeviceId() }));
            if (result?.error || !result?.payload) return;
            const payload = result.payload;
            if (payload.emailNotVerified) {
                notify('Your email is not verified yet. Enter the code we sent you to verify it.', 'error');
                navigate(Routes.auth.verifyEmail, { state: { email } });
                return;
            }
            if (payload.challenge === '2FA') {
                navigate(Routes.auth.verify2fa, { state: { email, methods: payload.methods, pendingToken: payload.pendingToken } });
                return;
            }
            notify('Logged in successfully!', 'success');
            if (payload.mustChangePassword === true) {
                navigate(Routes.account.changePassword, { replace: true, state: { forcePasswordChange: true } });
                return;
            }
            navigate(postAuthPath(Routes.home.main, Routes.auth.acceptMemberInvite, Routes.auth.acceptInvite), { replace: true });
        } catch (error: any) {
            notify(error.message, 'error');
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    if (initialLoading) {
        return <MuiLoader absolute loading={initialLoading} />;
    }

    return (
        <AuthLayaout>
            <form onSubmit={handleSubmit(submit)} className="Auth-form">
                <p className="Auth-form-title">Sign in to continue</p>
                <Controller
                    name="email"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField label="Username" value={value} onChange={onChange} error={errors.email?.message} />
                    )}
                />
                <Controller
                    name="password"
                    control={control}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                        <FloatingField
                            label="Password"
                            value={value}
                            isPassword={true}
                            onChange={onChange}
                            error={errors.password?.message}
                        />
                    )}
                />
                <Stack width="100%" alignItems="end">
                    <Button
                        fontSize={14}
                        color="info"
                        variant="text"
                        onClick={() => navigate(Routes.auth.forgotPassword)}
                        height="fit-content"
                        sx={{ '&:hover': { backgroundColor: 'transparent' }, p: '0px' }}
                    >
                        Forgot Password?
                    </Button>
                </Stack>
                <FormControlLabel
                    sx={{ my: 1 }}
                    control={
                        <Checkbox
                            defaultChecked
                            sx={{
                                color: '#F3F4FF',
                                '&.Mui-checked': {
                                    color: '#142456',
                                },
                            }}
                        />
                    }
                    label="Remember me and keep me logged in "
                    color="#F3F4FF"
                />

                <Button type="submit" disabled={isLoading} loading={isLoading} title="SIGN IN" />

                <p style={{ alignSelf: 'center' }}>
                    Not a member? Contact us
                    <Link
                        style={{ marginLeft: 4 }}
                        href="#"
                        color="inherit"
                        onClick={(e) => {
                            window.location.href = `mailto:${supportEmail}`;
                            e.preventDefault();
                        }}
                    >
                        {supportEmail}
                    </Link>
                </p>
            </form>
        </AuthLayaout>
    );
}

export default Login;
